import { ActionTree } from 'vuex'
import { IRootState } from '~/store'
import { IUserState } from '~/store/user/state'
import Storage from '~/lib/storage'
import { setLocale } from '~/plugins/i18n/persistLocale'
import OIDCScheme from '~/middleware/auth/schemes/oidc'

const actions: ActionTree<IUserState, IRootState> = {
  async load({ commit }) {
    try {
      const response = await this.$api.user.get()
      const { data } = response
      const {
        first_name: firstName,
        last_name: lastName,
        name,
        email,
        id
      } = data
      commit('setProfile', { firstName, lastName, name, email, id })

      if (data.language) {
        setLocale(this.$i18n, data.language)
      } else {
        await this.$api.user.put({ language: this.$i18n.locale })
      }

      const { rorg, role, access_type: accessType } = data
      commit('rorg', rorg)
      commit('role', role)
      commit('accessType', accessType)
    } catch (e) {}
  },

  async loadSources({ dispatch, commit, getters, rootGetters }) {
    commit('setLoadingSources', true)
    // Promise all will immediately call the catch if a single promise get rejected (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/all#Promise.all_fail-fast_behaviour)
    // so we need to catch every single promise in error cases to avoid an early call of setLoadingSources commit.
    await dispatch('load', null)
    await dispatch('organization/reload', null, { root: true })

    const { id, rorg, role } = getters
    const rootOrg = rootGetters['organization/byId'](rorg)
    const { type: rootOrgType } = rootOrg || {}

    // Set the root organization type
    commit('rootOrgType', rootOrgType)

    // Track user in Mixpanel
    this.$mixpanel?.identifyUser(id, {
      RootOrg: rorg,
      RootOrgType: rootOrgType,
      Role: role,
      UserId: id
    })

    commit('setLoadingSources', false)
  },

  /**
   * IMPORTANT: Call this action instead of $auth.logout() to ensure that the user data is cleaned up properly
   */
  async logout({ getters, commit, dispatch }) {
    const FCMToken = this.$platform.native.notifications?.token
    const refreshToken = (this.$auth.strategy as OIDCScheme).refreshToken.get()

    const loggingOutMutex = getters.loggingOutMutex

    // prevents logout API from being called multiple times
    if (!loggingOutMutex) {
      commit('loggingOutMutex', true)
      commit('setLoadingSources', true)
      // call logout endpoint, continue even if it fails
      try {
        await this.$api.user.logout({
          fcm_token: FCMToken,
          refresh_token: refreshToken
        })
      } catch (e) {}

      // clear user data
      dispatch('clear')
      // clear all existing locations
      dispatch('organization/clear', null, { root: true })
      sessionStorage.clear()

      this.$mixpanel?.resetUser()
      await this.$auth.logout()
      // release the lock after it is done
      commit('loggingOutMutex', false)
    }
  },

  clear({ commit }) {
    commit('eula', false)

    // remove from all used storages
    Storage.removeItem('user/eula')
  },

  setEula({ commit }, eula) {
    const isTrueSet = eula === 'true' || eula === true
    commit('eula', isTrueSet)
    Storage.setItem('user/eula', isTrueSet)
  },

  async remove({ dispatch }) {
    try {
      const { status } = await this.$api.user.delete()

      if (status !== 200) {
        return dispatch(
          'notify/error',
          this.$i18n.tc('user.profile.remove_submit_error'),
          { root: true }
        )
      }

      dispatch('logout')
      dispatch(
        'notify/success',
        this.$i18n.tc('user.profile.remove_submit_success'),
        { root: true }
      )
    } catch {
      dispatch(
        'notify/error',
        this.$i18n.tc('user.profile.remove_submit_error'),
        { root: true }
      )
    }
  },
  updateProfile({ commit }, { firstName, lastName, name, email }) {
    commit('setProfile', { firstName, lastName, name, email })
  }
}

export default actions
